import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import HomeButton from './common/HomeButton';
import { authService } from '../api/authService';

interface AccessControlProps {
  children: React.ReactNode;
}

const AccessControl: React.FC<AccessControlProps> = ({ children }) => {
  const location = useLocation();
  const currentUser = authService.getCurrentUser();

  // Pagine che tutti possono vedere
  const allowedPagesForAll = [
    '/dashboard',
    '/mio-orario',
    // Consenti l'accesso a Orario Dipendente a tutti gli utenti che hanno AbilitaInsOrari attivo
    ...(currentUser?.abilitaInsOrari ? ['/orario-dipendente', '/orario-dipendenti-taste'] : [])
  ];
  
  // Ruoli con accesso completo (a tutte le pagine gestionali)
  const privilegedRoles = ['Admin', 'Coordinatore'];

  // Ruoli limitati: accesso gestionale solo se hanno AbilitaInsOrari=1
  // Include Dipendente, Linker, Maker, Referente - i permessi sono determinati da AbilitaInsOrari
  const limitedRoles = ['Dipendente', 'Linker', 'Maker', 'Referente'];

  // Se l'utente non è autenticato, non dovrebbe essere qui (gestito da ProtectedRoute)
  if (!currentUser) {
    // Still show HOME for convenience when unauthenticated routes are wrapped accidentally
    return <>
      {children}
      <HomeButton />
    </>;
  }

  // Pagine riservate esclusivamente ad Admin (non visibili dalla dashboard, accesso diretto via URL)
  const adminOnlyPages = ['/gdv-outbox-log'];

  // Se l'utente ha un ruolo privilegiato, può accedere a tutto
  // tranne le pagine admin-only che sono riservate solo ad Admin
  if (privilegedRoles.includes(currentUser.ruolo)) {
    // Coordinatore non può accedere alle pagine admin-only
    if (adminOnlyPages.includes(location.pathname) && currentUser.ruolo !== 'Admin') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 3, backgroundColor: '#f5f5f5' }}>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center', maxWidth: 600, borderRadius: 2 }}>
            <Box sx={{ marginBottom: 3 }}>
              <img src="/nonquigif" alt="Accesso negato" style={{ maxWidth: '300px', width: '100%', height: 'auto', borderRadius: '8px' }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: 2 }}>Accesso Negato</Typography>
            <Typography variant="h6" sx={{ marginBottom: 2, color: '#666' }}>
              Questa pagina è riservata agli Amministratori.
            </Typography>
          </Paper>
          <HomeButton />
        </Box>
      );
    }
    return <>
      {children}
      <HomeButton />
    </>;
  }

  // Se ruolo limitato (Dipendente, Linker, Maker, Referente), consenti Generazione Orari e Gestione Dipendenti solo se abilitato
  if (limitedRoles.includes(currentUser.ruolo)) {
    const canManage = currentUser.abilitaInsOrari === true;
    // Esteso: include quantità personale, cambi turno e turni
    const extraWhenEnabled = canManage 
      ? ['/generazione-orario', '/dipendenti', '/quantita-personale', '/gestione-cambi-turno', '/turni', '/orario-dipendente', '/orario-dipendenti-taste']
      : [];
    // Le pagine admin-only non sono mai accessibili ai ruoli limitati
    const allowed = [...allowedPagesForAll, ...extraWhenEnabled];
    if (!allowed.includes(location.pathname)) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 3, backgroundColor: '#f5f5f5' }}>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center', maxWidth: 600, borderRadius: 2 }}>
            <Box sx={{ marginBottom: 3 }}>
              <img src="/nonquigif" alt="Accesso negato" style={{ maxWidth: '300px', width: '100%', height: 'auto', borderRadius: '8px' }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: 2 }}>Accesso Negato</Typography>
            <Typography variant="h6" sx={{ marginBottom: 2, color: '#666' }}>
              Non hai i permessi necessari per visualizzare questa pagina
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 2, color: '#888' }}>
              Puoi accedere alla <strong>Dashboard</strong> o visualizzare <strong>Il Mio Orario</strong>.
            </Typography>
          </Paper>
          <HomeButton />
        </Box>
      );
    }
    return <>
      {children}
      <HomeButton />
    </>;
  }

  // Fallback: per qualsiasi ruolo non gestito sopra, mostra il contenuto base
  // Se l'utente può accedere alla pagina corrente, mostra il contenuto
  return <>
    {children}
    <HomeButton />
  </>;
};

export default AccessControl;
